import React, { useRef, useState, useEffect } from "react"
import { ChevronDown, Search } from 'lucide-react'
import styles from "../../_css/ReportingAndSterring.module.css"
import { useClickOutsideWithCallback } from "../hooks/useClickOutside";
import ResponseLoader from '../common/ResponseLoader.js';
import { getHeyGenUsageData, getCompaniesList } from "../../requests.js";
import { useDashboardFilters } from './context/DashboardContext';
import { useDispatch } from 'react-redux';
import { startLoader, stopLoader } from "../../_store/reducers/loaderReducer";

const HeyGenUses = () => {
    const [showDropdown, setShowDropdown] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedOption, setSelectedOption] = useState('Top 3');
    const [companiesList, setCompaniesList] = useState([]);
    const [apiData, setApiData] = useState(null);
    const [filteredCompanyData, setFilteredCompanyData] = useState([]);
    const [loading, setLoading] = useState(false);
    const { filters } = useDashboardFilters();
    const dispatch = useDispatch();
    const dropDownRef = useRef(null);

    // Check if global filter has a specific company selected (not 'all')
    const hasGlobalCompanyFilter = filters.company && filters.company !== 'all';

    const toggleDropdown = () => {
        if (!hasGlobalCompanyFilter) {
            setShowDropdown(!showDropdown);
        }
    };

    useClickOutsideWithCallback(dropDownRef, () => {
        setShowDropdown(false);
    });

    // Fetch companies list on component mount
    useEffect(() => {
        const fetchCompanies = async () => {
            try {
                const companies = await getCompaniesList();
                if (companies && companies.result) {
                    setCompaniesList(companies.result);
                }
            } catch (err) {
                console.error('Error fetching companies:', err);
            }
        };

        fetchCompanies();
    }, []);

    // Fetch HeyGen usage data when filters change
    useEffect(() => {
        fetchHeyGenData();
    }, [filters]);
    console.log(filters)
    // Update filtered data when selection changes or API data updates
    useEffect(() => {
        if (apiData && apiData.companyData) {
            applyFilter(hasGlobalCompanyFilter ? filters.company : selectedOption, apiData.companyData);
        }
    }, [apiData, selectedOption, filters.company, hasGlobalCompanyFilter]);

    // Function to fetch HeyGen usage data
    const fetchHeyGenData = async () => {
        setLoading(true);
        dispatch(startLoader());
        try {
            const startDate = filters.dateRange?.from;
            const endDate = filters.dateRange?.to;
            const companyName = filters.company;
            const response = await getHeyGenUsageData({ startDate, endDate, companyName });

            if (response?.success) {
                setApiData(response);
            } else {
                console.error('Error in API response:', response);
                setApiData(null);
            }
        } catch (err) {
            console.error('Error fetching HeyGen usage data:', err);
            setApiData(null);
        } finally {
            setLoading(false);
            dispatch(stopLoader());
        }
    };

    // Apply filter based on selected option
    const applyFilter = (option, data) => {
        if (option === 'Top 3') {
            // Show top 3 companies (already sorted by RolePlays DESC from API)
            setFilteredCompanyData(data.slice(0, 3));
        } else {
            // Filter for specific company
            const filtered = data.filter(item =>
                item.company?.toLowerCase() === option.toLowerCase()
            );
            setFilteredCompanyData(filtered);
        }
    };

    // Get dropdown options
    const getOptions = () => {
        const companyNames = companiesList.map(company => company.name);
        return ['Top 3', ...companyNames];
    };

    const options = getOptions();

    // Filter options based on search term
    const filteredOptions = options.filter(option =>
        option.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleOptionSelect = (option) => {
        if (!hasGlobalCompanyFilter) {
            setSelectedOption(option);
            setSearchTerm('');
            setShowDropdown(false);
        }
    };

    // Get display text for dropdown button
    const getDropdownButtonText = () => {
        if (hasGlobalCompanyFilter) {
            return filters.company;
        }
        return selectedOption;
    };

    return (
        <div className={styles.heyGenContainer}>
            <div className={styles.heyGenHeader}>
                <h5 className={styles.heyGenTitle}>
                    HeyGen Usage
                </h5>

                {/* Dropdown */}
                <div className={styles.dropdownWrapper} ref={dropDownRef}>
                    <button
                        onClick={toggleDropdown}
                        className={styles.dropdownButton}
                        disabled={hasGlobalCompanyFilter}
                        style={{
                            cursor: hasGlobalCompanyFilter ? 'not-allowed' : 'pointer',
                            opacity: hasGlobalCompanyFilter ? 0.6 : 1
                        }}
                    >
                        {getDropdownButtonText()}
                        {!hasGlobalCompanyFilter && <ChevronDown size={16} color="#6b7280" />}
                    </button>

                    {showDropdown && !hasGlobalCompanyFilter && (
                        <div className={styles.dropdownMenu}>
                            {/* Search input */}
                            <div className={styles.searchWrapper}>
                                <input
                                    type="text"
                                    placeholder="Search companies..."
                                    value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                    className={styles.searchInput}
                                />
                                <Search
                                    size={16}
                                    color="#6B7280"
                                    className={styles.searchIcon}
                                />
                            </div>

                            <div className={styles.dropdownOptions}>
                                {filteredOptions.map((option, idx) => (
                                    <div
                                        key={idx}
                                        onClick={() => handleOptionSelect(option)}
                                        className={`${styles.dropdownOption} ${selectedOption === option ? styles.activeOption : ''}`}
                                        onMouseEnter={e => {
                                            if (selectedOption !== option) {
                                                e.currentTarget.style.backgroundColor = '#E0F2FE';
                                                e.currentTarget.style.color = '#0D9488';
                                                e.currentTarget.style.fontWeight = 600;
                                            }
                                        }}
                                        onMouseLeave={e => {
                                            if (selectedOption !== option) {
                                                e.currentTarget.style.backgroundColor = 'transparent';
                                                e.currentTarget.style.color = '#374151';
                                                e.currentTarget.style.fontWeight = 400;
                                            }
                                        }}
                                    >
                                        {option}
                                    </div>
                                ))}
                                {searchTerm && filteredOptions.length === 0 && (
                                    <div className={styles.noResults}>
                                        No companies found for "{searchTerm}"
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Metric Cards - Directly from API */}
            {apiData?.metricCards && (
                <div className={styles.metricCardsGrid}>
                    {apiData.metricCards.map((card, index) => (
                        <div
                            key={index}
                            className={styles.metricCard}
                            style={{ backgroundColor: card.bgColor }}
                        >
                            <div className={styles.metricLabel}>
                                {card.label}
                            </div>
                            <div className={styles.metricValue}>
                                {card.value}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Table */}
            <div className={styles.heyGenTableContainer}>
                <table className={styles.heyGenTable}>
                    <thead>
                        <tr>
                            <th className={styles.tableHeader}>
                                Company
                            </th>
                            {["Tokens Used", "RolePlays", "Top Avatars", "Last Role Plays"].map((label, i) => (
                                <th
                                    key={i}
                                    className={styles.tableHeader}
                                >
                                    {label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {filteredCompanyData.map((item, idx) => (
                            <tr key={idx} className={styles.tableRow}>
                                <td className={styles.companyCell}>
                                    {item.company}
                                </td>
                                <td className={styles.tableCell}>{item.tokensUsed}</td>
                                <td className={styles.tableCell}>{item.RolePlays}</td>
                                <td className={styles.tableCell}>{item.TopAvatars}</td>
                                <td className={styles.tableCell}>{item.LastRolePlay}</td>
                            </tr>
                        ))}
                        {filteredCompanyData.length === 0 && !loading && (
                            <tr>
                                <td colSpan="5" className={styles.noDataCell}>
                                    {hasGlobalCompanyFilter
                                        ? `No data available for company: ${filters.company}`
                                        : 'No company data available'
                                    }
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>

                {loading && (
                    <div className={styles.loaderContainer}>
                        <ResponseLoader />
                    </div>
                )}
            </div>
        </div>
    );
}

export default HeyGenUses;